import { useMemo } from "react";
import { JSONPath } from "jsonpath-plus";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { DetailsCardRow } from "@covertree/ui/content/DetailsCardRow";
import {
  PolicyLevelCoverages,
  PolicyPlan,
  UnitLevelCoverages,
} from "@covertree/new-business-graphql";
import {
  CoverageLevel,
  getMineSubsidenceOption,
  getMineSubsidenceScheduledOption,
  getRoofPaymentScheduleOption,
  mapPolicyPlanToPlanName,
} from "@covertree/new-business-mappings";

import { getAddons, getPlanOptions } from "./utils";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

interface PlanDetailsProps {
  formattedStartDate: string;
  policyPlan: PolicyPlan;
  policyLevelCoverages: PolicyLevelCoverages;
  unitLevelCoverages: UnitLevelCoverages;
  onEditMyPlan(): void;
}

export function PlanDetails({
  formattedStartDate,
  policyPlan,
  policyLevelCoverages,
  unitLevelCoverages,
  onEditMyPlan,
}: PlanDetailsProps) {
  const { units } = useNewBusinessSelector(selectPolicy)!;

  const coverageOptions = useMemo(
    () => ({
      mineSubsidenceOption: getMineSubsidenceOption(units[0]),
      mineSubsidenceScheduledOption: getMineSubsidenceScheduledOption(units[0]),
      isRoofPaymentCustomizable:
        units[0].unitLevelCoverages.roofPaymentSchedule.valueIsCustomizable,
      roofPaymentScheduleOption: getRoofPaymentScheduleOption(units[0]),
    }),
    [units]
  );

  const planOptions = useMemo(
    () => getPlanOptions(unitLevelCoverages),
    [unitLevelCoverages]
  );

  const addons = useMemo(
    () => getAddons(unitLevelCoverages, policyLevelCoverages),
    [unitLevelCoverages, policyLevelCoverages]
  );

  const planOptionsComponent = planOptions.map((coverage) => (
    <Box key={coverage.accessor}>
      <DetailsCardRow
        name={coverage.getName(coverageOptions)}
        tooltip={coverage.getTooltip(coverageOptions)}
        items={coverage.items}
        valueFormats={coverage.getValueFormats(
          JSONPath({
            path: coverage.accessor,
            json: unitLevelCoverages,
          })[0]
        )}
      />
    </Box>
  ));

  const addonsComponent = addons.map((coverage) => (
    <Box key={coverage.accessor}>
      <DetailsCardRow
        name={coverage.getName(coverageOptions)}
        tooltip={coverage.getTooltip(coverageOptions)}
        valueFormats={coverage.getValueFormats(
          JSONPath({
            path: coverage.accessor,
            json:
              coverage.level === CoverageLevel.Unit
                ? unitLevelCoverages
                : policyLevelCoverages,
          })[0]
        )}
      />
    </Box>
  ));

  return (
    <Card>
      <CardContent>
        <Stack direction="column" spacing={3}>
          <Stack direction="column">
            <Typography variant="h3">
              {mapPolicyPlanToPlanName(policyPlan)}
            </Typography>
            <Stack
              spacing={1}
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
              }}
              justifyContent="space-between">
              <Stack direction="column">
                <Typography variant="h6">Coverage effective date</Typography>
                <Typography variant="h4" color="secondary">
                  {formattedStartDate}
                </Typography>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  width: {
                    xs: "100%",
                    md: "auto",
                  },
                }}
                data-testid="edit-my-plan-button"
                onClick={onEditMyPlan}>
                Edit my plan
              </Button>
            </Stack>
          </Stack>
          <Divider />
          <Stack direction="column" spacing={1}>
            {planOptionsComponent}
          </Stack>
          {addons.length !== 0 && (
            <>
              <Typography variant="h4" sx={{ mt: 4 }}>
                Add ons
              </Typography>
              <Divider />
              <Stack direction="column" spacing={1}>
                {addonsComponent}
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
